import type { UnitSystem } from './obd/pids.ts';

// Maintenance status: how far through an interval a vehicle is, and which axis
// says so.
//
// Kept pure and free of React so the selftest can exercise it. Everything here
// works in kilometres, because that is how the schedule and the odometer are
// both stored; conversion happens only at the point of display.

export const MI_PER_KM = 0.621371;

export interface MaintenanceItem {
  id: number;
  make: string;
  model: string | null;
  item: string;
  category: string;
  intervalKm: number | null;
  /** As the manufacturer printed it in miles, when they printed one. */
  intervalMi?: number | null;
  intervalMonths: number | null;
  intervalHours: number | null;
  /** Set when the manufacturer states a condition instead of an interval. */
  trigger: string | null;
  note: string | null;
}

export interface VehicleUsage {
  /** Latest odometer reading in kilometres, or null if none recorded. */
  odometerKm: number | null;
  /** Engine hours, if the vehicle reports them. Almost none do. */
  engineHours: number | null;
  /** When the item was last done, and at what reading. */
  lastDoneAt: string | null;
  lastDoneKm: number | null;
  lastDoneHours: number | null;
  /** Fallback anchor when nothing has been recorded as done. */
  firstSeenAt: string;
}

export type MaintenanceState = 'overdue' | 'due' | 'soon' | 'ok' | 'on-demand' | 'unknown';

export interface MaintenanceStatus {
  item: MaintenanceItem;
  state: MaintenanceState;
  /** 1.0 means exactly at the interval. Null when no axis applies. */
  progress: number | null;
  /** Which axis produced `progress`. */
  axis: 'distance' | 'time' | 'hours' | null;
  /** Plain sentence naming the axis and the numbers behind it. */
  detail: string;
  /**
   * The interval has elapsed more than twice with nothing recorded as done.
   *
   * Almost always a gap in the record rather than a vehicle that has covered
   * 88,000 km without a cabin filter. Kept separate so items genuinely due in
   * the current cycle are not buried under a decade of unrecorded history.
   */
  likelyServiced: boolean;
  /** How far through the current cycle, for a repeating interval. */
  cyclePosition: number | null;
}

const MS_PER_MONTH = 30.44 * 24 * 60 * 60 * 1000;

/**
 * What each trigger means, in the manufacturer's own terms.
 *
 * A trigger is the manufacturer declining to publish an interval, which is a
 * real answer rather than missing data - and the reason differs by make, so
 * the sentence should too. Anything unlisted falls back to a general phrasing
 * rather than being treated as unknown.
 */
const TRIGGER_DETAIL: Record<string, string> = {
  'oil-life-monitor': 'Done at each oil change, which the vehicle decides from its oil-life monitor.',
  // Named for the system, not the marque. BMW and MINI both use Condition
  // Based Servicing, and a MINI owner reading "BMW Condition Based Servicing"
  // against their own car reads it as the wrong record.
  'condition-based-service':
    'Condition Based Servicing: the car measures how it has been driven and reports this one when it is due.',
  'maintenance-minder':
    'Honda Maintenance Minder: the car calculates remaining oil life and shows a code when this one is due.',
};

function monthsBetween(fromIso: string, now: Date): number {
  const from = new Date(fromIso).getTime();
  if (!Number.isFinite(from)) return 0;
  return Math.max(0, (now.getTime() - from) / MS_PER_MONTH);
}

/**
 * Distance in the reader's units.
 *
 * Every stored figure is kilometres. This is the only place that changes, so a
 * conversion cannot drift between the schedule, the odometer and the prose.
 */
/**
 * An interval as the manufacturer published it for this reader's units.
 *
 * Several publish both columns and they are not exact equivalents - GM prints
 * "12 000 km/7,500 mi", and 12,000 km is 7,456 miles. Converting the metric
 * figure shows a US owner 7,456 against a manual saying 7,500, which reads as
 * an error in the tool rather than rounding by the manufacturer.
 *
 * So the published figure wins when there is one, and conversion is the
 * fallback for sources that printed only one column.
 */
function publishedDistance(item: MaintenanceItem, units: UnitSystem): string | null {
  if (units === 'imperial' && item.intervalMi != null) {
    return `${item.intervalMi.toLocaleString()} mi`;
  }
  if (item.intervalKm === null) return null;
  return formatDistance(item.intervalKm, units);
}

export function formatDistance(km: number, units: UnitSystem, withUnit = true): string {
  const value = units === 'imperial' ? km * MI_PER_KM : km;
  const rounded = Math.round(value).toLocaleString();
  if (!withUnit) return rounded;
  return `${rounded} ${units === 'imperial' ? 'mi' : 'km'}`;
}

export function formatInterval(item: MaintenanceItem, units: UnitSystem): string {
  const parts: string[] = [];
  const distance = publishedDistance(item, units);
  if (distance) parts.push(distance);
  if (item.intervalMonths !== null) {
    parts.push(item.intervalMonths % 12 === 0 ? `${item.intervalMonths / 12} yr` : `${item.intervalMonths} mo`);
  }
  if (item.intervalHours !== null) parts.push(`${item.intervalHours.toLocaleString()} h`);

  if (parts.length === 0) return item.trigger === 'oil-life-monitor' ? 'When the vehicle asks' : 'No interval stated';
  // Ford's own wording, and the reason precedence is a maximum rather than a
  // sum or an average.
  return parts.length > 1 ? `${parts.join(' / ')} — whichever comes first` : parts[0];
}

/**
 * Order of precedence.
 *
 * Every axis an item defines is measured independently, and the one furthest
 * through its interval wins. That is the manufacturer's own rule: "whichever
 * comes first" means the earliest axis to arrive decides, which is the largest
 * fraction of an interval consumed.
 *
 * Averaging them would let a low-mileage vehicle hide an overdue time item,
 * which is precisely the failure this feature exists to prevent.
 */
export function statusFor(
  item: MaintenanceItem,
  usage: VehicleUsage,
  now = new Date(),
  // The detail sentence quotes distances, so it has to speak the same units as
  // the interval above it. Reading "9,942 mi" over "88,514 km of 16,000 km" is
  // the kind of mismatch that makes someone distrust the whole number.
  units: UnitSystem = 'metric',
): MaintenanceStatus {
  // A trigger with no interval is not a percentage of anything. Saying "when
  // the vehicle asks" is honest; inventing a number is not.
  if (item.intervalKm === null && item.intervalMonths === null && item.intervalHours === null) {
    // Any trigger, not just Ford's. This special-cased 'oil-life-monitor'
    // alone, so BMW's condition-based-service rows - five of them, and the
    // whole of what BMW publishes - fell through to "unknown" and sat in the
    // actionable list reading "No interval stated". That looks like a defect
    // and is really a manufacturer saying the car decides.
    if (item.trigger) {
      return {
        item,
        state: 'on-demand',
        progress: null,
        axis: null,
        detail: TRIGGER_DETAIL[item.trigger] ?? 'The vehicle decides when this is due, rather than a fixed interval.',
        likelyServiced: false,
        cyclePosition: null,
      };
    }
    return {
      item, state: 'unknown', progress: null, axis: null,
      detail: 'No interval recorded for this item.', likelyServiced: false, cyclePosition: null,
    };
  }

  const anchorDate = usage.lastDoneAt ?? usage.firstSeenAt;
  const candidates: { axis: 'distance' | 'time' | 'hours'; progress: number; detail: string }[] = [];

  if (item.intervalKm !== null && usage.odometerKm !== null) {
    const since = usage.odometerKm - (usage.lastDoneKm ?? 0);
    candidates.push({
      axis: 'distance',
      progress: since / item.intervalKm,
      // The elapsed side is measured, so it converts; the target side is the
      // manufacturer's own figure and is quoted as published.
      detail: `${formatDistance(since, units, false)} of ${publishedDistance(item, units) ?? formatDistance(item.intervalKm, units)}`,
    });
  }

  if (item.intervalMonths !== null) {
    const months = monthsBetween(anchorDate, now);
    candidates.push({
      axis: 'time',
      progress: months / item.intervalMonths,
      detail: `${months.toFixed(1)} months of ${item.intervalMonths}`,
    });
  }

  if (item.intervalHours !== null && usage.engineHours !== null) {
    const since = usage.engineHours - (usage.lastDoneHours ?? 0);
    candidates.push({
      axis: 'hours',
      progress: since / item.intervalHours,
      detail: `${Math.round(since).toLocaleString()} of ${item.intervalHours.toLocaleString()} engine hours`,
    });
  }

  // An item with intervals we cannot measure - a distance interval on a vehicle
  // with no odometer reading - is unknown rather than not due. Reporting "ok"
  // here would be a guess dressed as a fact.
  if (candidates.length === 0) {
    const missing = item.intervalKm !== null ? 'an odometer reading' : 'engine hours';
    return {
      item, state: 'unknown', progress: null, axis: null,
      detail: `Needs ${missing} before this can be judged.`, likelyServiced: false, cyclePosition: null,
    };
  }

  const winner = candidates.reduce((a, b) => (b.progress > a.progress ? b : a));

  // Two full intervals with no record is the tell. Nobody drives 88,000 km on
  // one cabin filter, so the reading is evidence about the paperwork rather
  // than about the vehicle.
  //
  // Never applied to the time axis alone: three years is three years, and a
  // stored car really can go that long. Only a repeating distance or hours
  // interval earns this.
  const repeating = winner.axis === 'distance' || winner.axis === 'hours';
  const likelyServiced = repeating && !usage.lastDoneAt && winner.progress >= 2;

  // Where the vehicle sits in the current cycle, which is the useful number
  // once the earlier cycles are assumed serviced.
  const cyclePosition = repeating ? winner.progress % 1 : null;
  const judged = likelyServiced ? (cyclePosition ?? 0) : winner.progress;

  return {
    item,
    state: judged >= 1.25 ? 'overdue' : judged >= 1 ? 'due' : judged >= 0.8 ? 'soon' : 'ok',
    progress: winner.progress,
    axis: winner.axis,
    detail: likelyServiced
      ? `${winner.detail}. That interval has passed ${Math.floor(winner.progress)} times with nothing recorded, so this is probably a gap in the record.`
      : winner.detail,
    likelyServiced,
    cyclePosition,
  };
}

const ORDER: Record<MaintenanceState, number> = {
  overdue: 0,
  due: 1,
  soon: 2,
  unknown: 3,
  'on-demand': 4,
  ok: 5,
};

/** Most urgent first, then furthest through its interval. */
export function sortStatuses(list: MaintenanceStatus[]): MaintenanceStatus[] {
  return [...list].sort((a, b) => ORDER[a.state] - ORDER[b.state] || (b.progress ?? 0) - (a.progress ?? 0));
}

export const STATE_LABEL: Record<MaintenanceState, string> = {
  overdue: 'Overdue',
  due: 'Due now',
  soon: 'Coming up',
  ok: 'Not due',
  'on-demand': 'On demand',
  unknown: 'Unknown',
};
